import {
  FileText,
  Calculator,
  FileSpreadsheet,
  ShoppingCart,
  Wrench,
  ClipboardList,
  ClipboardCheck,
  PenTool,
  Ruler,
  ShieldCheck,
  BadgeCheck,
  ShoppingBag,
  Factory,
  Cog,
  Wallet,
  Users,
  Truck,
  Package,
  Box,
  Boxes,
  Warehouse,
  BarChart3,
  Database,
  Layers,
  Receipt,
  FileCheck,
  Settings,
  ClipboardPen,
  Send,
} from "lucide-react";

// Accent palette used when a keyword match doesn't define its own colour.
const PALETTE = [
  "#3b82f6", // blue
  "#10b981", // green
  "#8b5cf6", // purple
  "#f59e0b", // orange
  "#ef4444", // red
  "#14b8a6", // teal
  "#6366f1", // indigo
  "#ec4899", // pink
];

// Keyword-based mapping: first matching keyword in the (lowercased) title wins.
const KEYWORD_META = [
  {
    match: ["quotation comparison", "purchase comparison"],
    Icon: ClipboardCheck,
    accent: "#0e7490",
    desc: "Compare Vendor Offers & Generate Purchase Orders",
  },
  {
    match: ["enquiry", "inquiry"],
    Icon: FileText,
    accent: "#3b82f6",
    desc: "Create & Manage Enquiries",
  },
  {
    match: ["estimation", "costing"],
    Icon: Calculator,
    accent: "#10b981",
    desc: "Create Estimation Costing",
  },
  {
    match: ["quotation", "quote"],
    Icon: FileSpreadsheet,
    accent: "#8b5cf6",
    desc: "Create & Manage Quotations",
  },
  {
    match: ["customer order", "sales order"],
    Icon: ShoppingCart,
    accent: "#f59e0b",
    desc: "Manage Customer Orders",
  },
  {
    match: ["work order"],
    Icon: Wrench,
    accent: "#ef4444",
    desc: "Create & Manage Work Orders",
  },
  {
    match: ["purchase order", "purchase"],
    Icon: ShoppingBag,
    accent: "#0ea5e9",
    desc: "Manage Purchase Orders",
  },
  {
    match: ["invoice"],
    Icon: Receipt,
    accent: "#f97316",
    desc: "Create & Manage Invoices",
  },
  {
    match: ["delivery", "dispatch"],
    Icon: Send,
    accent: "#14b8a6",
    desc: "Manage Deliveries",
  },
  {
    match: ["grn", "goods receipt", "inward"],
    Icon: Package,
    accent: "#22c55e",
    desc: "Manage Goods Receipts",
  },
  {
    match: ["bom", "bill of material"],
    Icon: Layers,
    accent: "#6366f1",
    desc: "Manage Bill of Materials",
  },
  {
    match: ["process card"],
    Icon: Cog,
    accent: "#8b5cf6",
    desc: "Define Manufacturing Process",
  },
  {
    match: ["route card"],
    Icon: ClipboardCheck,
    accent: "#0ea5e9",
    desc: "Manage Operation Routing",
  },
  {
    match: ["job card", "job work"],
    Icon: ClipboardPen,
    accent: "#f59e0b",
    desc: "Track Job Cards",
  },
  {
    match: ["inspection", "quality"],
    Icon: ShieldCheck,
    accent: "#10b981",
    desc: "Manage Inspection Reports",
  },
  {
    match: ["drawing", "design", "engineering"],
    Icon: PenTool,
    accent: "#6366f1",
    desc: "Manage Drawings & Design",
  },
  {
    match: ["parameter"],
    Icon: Ruler,
    accent: "#0ea5e9",
    desc: "Configure Parameters",
  },
  {
    match: ["approval", "approve"],
    Icon: BadgeCheck,
    accent: "#22c55e",
    desc: "Review & Approve",
  },
  {
    match: ["customer"],
    Icon: Users,
    accent: "#3b82f6",
    desc: "Manage Customer Master",
  },
  {
    match: ["vendor", "supplier"],
    Icon: Truck,
    accent: "#f59e0b",
    desc: "Manage Vendor Master",
  },
  {
    match: ["product"],
    Icon: Box,
    accent: "#8b5cf6",
    desc: "Manage Product Master",
  },
  {
    match: ["stock", "inventory", "store"],
    Icon: Warehouse,
    accent: "#14b8a6",
    desc: "Manage Inventory & Stock",
  },
  {
    match: ["material"],
    Icon: Boxes,
    accent: "#0ea5e9",
    desc: "Manage Materials",
  },
  {
    match: ["report", "analysis"],
    Icon: BarChart3,
    accent: "#6366f1",
    desc: "View Reports & Analytics",
  },
  {
    match: ["production"],
    Icon: Factory,
    accent: "#ef4444",
    desc: "Plan & Monitor Production",
  },
  {
    match: ["account", "payment", "finance"],
    Icon: Wallet,
    accent: "#22c55e",
    desc: "Manage Accounts",
  },
  {
    match: ["maintenance"],
    Icon: Wrench,
    accent: "#f97316",
    desc: "Schedule Maintenance",
  },
  {
    match: ["master", "reference"],
    Icon: Database,
    accent: "#8b5cf6",
    desc: "Manage Reference Data",
  },
  {
    match: ["setting", "config"],
    Icon: Settings,
    accent: "#64748b",
    desc: "Configure Settings",
  },
];

const FALLBACK_ICONS = [
  FileText,
  ClipboardList,
  FileCheck,
  Package,
  Cog,
  Layers,
  BarChart3,
  Box,
];

/**
 * Returns { Icon, accent, description } for a menu tile based on its title.
 * Falls back to a cycling palette + icon when no keyword matches.
 */
export const getTileMeta = (title = "", index = 0) => {
  const key = String(title).toLowerCase();

  for (const meta of KEYWORD_META) {
    if (meta.match.some((word) => key.includes(word))) {
      return { Icon: meta.Icon, accent: meta.accent, description: meta.desc };
    }
  }

  return {
    Icon: FALLBACK_ICONS[index % FALLBACK_ICONS.length],
    accent: PALETTE[index % PALETTE.length],
    description: `Create & Manage ${title}`,
  };
};
